import * as React from "react";
import { Calendar as CalendarIcon, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { EURO_DATE_PATTERN, formatEuroDate, parseDateOnly, toDateOnly } from "@/lib/date";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export interface DatePickerProps {
    /** Selected date as an ISO date-only string ("YYYY-MM-DD"), or null when unset. */
    value: string | null;
    /** Called with the new ISO date-only string, or null when cleared. */
    onChange: (value: string | null) => void;
    id?: string;
    /** Trigger placeholder when no date is set (defaults to "dd/mm/yyyy"). */
    placeholder?: string;
    /** Show an inline clear (×) button when a date is set. */
    clearable?: boolean;
    /** Style the trigger as invalid/attention (e.g. an overdue due date). */
    invalid?: boolean;
    disabled?: boolean;
    className?: string;
    /** Accessible name when there is no associated <Label htmlFor>. */
    "aria-label"?: string;
}

/**
 * A single-date picker (Popover + Calendar) that reads and writes the domain's
 * ISO date-only string ("YYYY-MM-DD") and DISPLAYS it in European format
 * (dd/MM/yyyy). Drop-in replacement for `<input type="date">`. Week starts on
 * Monday, the European convention.
 */
export function DatePicker({
    value,
    onChange,
    id,
    placeholder = EURO_DATE_PATTERN,
    clearable = false,
    invalid = false,
    disabled = false,
    className,
    "aria-label": ariaLabel,
}: DatePickerProps) {
    const [open, setOpen] = React.useState(false);
    const selected = parseDateOnly(value);
    const showClear = clearable && !!selected && !disabled;

    const handleSelect = (date: Date | undefined) => {
        onChange(date ? toDateOnly(date) : null);
        setOpen(false);
    };

    return (
        <div className={cn("relative", className)}>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        id={id}
                        type="button"
                        variant="outline"
                        disabled={disabled}
                        aria-label={ariaLabel}
                        className={cn(
                            "w-full justify-start text-left font-normal",
                            !selected && "text-muted-foreground",
                            showClear && "pr-9",
                            invalid && "border-destructive text-destructive",
                        )}
                    >
                        <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                        {selected ? formatEuroDate(value) : <span>{placeholder}</span>}
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                        mode="single"
                        selected={selected}
                        defaultMonth={selected}
                        onSelect={handleSelect}
                        weekStartsOn={1}
                        autoFocus
                    />
                </PopoverContent>
            </Popover>
            {showClear && (
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label="Clear date"
                    onClick={() => onChange(null)}
                    className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                    <X className="h-4 w-4" />
                </Button>
            )}
        </div>
    );
}
